import { useEffect, useMemo, useState } from 'react';
import type { LocalePack, WidgetStrings } from './types';
import { getLoadedLocalePack, loadLocalePack, resolveLocalePackCode } from './loader';
import { resolveWidgetStrings } from '../utils/i18n';

interface LoadedLocale {
  code: string;
  pack: LocalePack;
}

/**
 * Resolves the widget string table for `language`. English is returned
 * synchronously; when the language has a lazily loaded pack the hook swaps it in
 * once the chunk resolves (same pattern as `useMerchantPreset`).
 */
export function useWidgetStrings(
  language: string | null | undefined,
  overrides: Record<string, unknown> | null | undefined
): { strings: WidgetStrings; stringsLoaded: boolean } {
  const packCode = useMemo(() => resolveLocalePackCode(language), [language]);
  const [loaded, setLoaded] = useState<LoadedLocale | null>(() => {
    const pack = getLoadedLocalePack(packCode);
    return pack && packCode ? { code: packCode, pack } : null;
  });

  useEffect(() => {
    if (!packCode || getLoadedLocalePack(packCode)) return;
    let cancelled = false;
    loadLocalePack(packCode)
      .then(pack => {
        if (!cancelled && pack) setLoaded({ code: packCode, pack });
      })
      .catch(() => {
        // Keep English; a failed chunk load must not break the widget.
      });
    return () => {
      cancelled = true;
    };
  }, [packCode]);

  const activePack = packCode
    ? loaded?.code === packCode
      ? loaded.pack
      : getLoadedLocalePack(packCode)
    : null;
  const strings = useMemo(
    () => resolveWidgetStrings(language, overrides, activePack),
    [language, overrides, activePack]
  );
  return { strings, stringsLoaded: !packCode || activePack !== null };
}
